// components/ClientCopyPanel.jsx — Client Copy History (equivalent of SCC3)
// Data: GET /api/sap/basis/client-copy?connectionId=<id>  (table CCCFLOW via RFC_READ_TABLE)
import React, { useMemo, useState } from 'react';
import { RefreshCw, Copy } from 'lucide-react';
import DetailSheet from './mobile/DetailSheet';

const fmt = (n) => Number(n || 0).toLocaleString();
function fmtDur(sec) {
  sec = Number(sec || 0);
  if (!sec) return '—';
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  return h ? `${h}h ${m}m` : m ? `${m}m ${s}s` : `${s}s`;
}
function statusTone(r) {
  if (r.status === 'O') return { bg: '#dcfce7', fg: '#166534' };
  if (r.status === 'V') return { bg: '#fef3c7', fg: '#92400e' };
  return { bg: '#fee2e2', fg: '#991b1b' };
}
export function ClientCopyStatus({ r }) {
  const t = statusTone(r);
  return <span style={{ background: t.bg, color: t.fg, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{r.statusText}</span>;
}

const TYPES = ['All', 'Local copy', 'Remote copy', 'Export', 'Import', 'Client delete', 'Copy by request'];

export default function ClientCopyPanel({ data, loading, error, onReload }) {
  const [type, setType] = useState('All');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);

  const rows = useMemo(() => {
    const list = data?.runs || [];
    const s = q.trim().toLowerCase();
    return list.filter(r => (type === 'All' || r.type === type) &&
      (!s || [r.targetClient, r.sourceClient, r.user, r.profile, r.sourceSystem, r.request].some(v => String(v || '').toLowerCase().includes(s))));
  }, [data, type, q]);

  const sm = data?.summary || {};
  const types = TYPES.filter(x => x === 'All' || (sm.byType || {})[x]);

  const th = { background: '#f8fafc', padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#64748b', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', textTransform: 'uppercase' };
  const td = { padding: '7px 10px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#1e293b', whiteSpace: 'nowrap' };

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, color: '#0f172a' }}>
          <Copy size={16} /> Client Copy History <span style={{ color: '#94a3b8', fontWeight: 500, fontSize: 12 }}>(SCC3 · table CCCFLOW)</span>
        </div>
        <div style={{ flex: 1 }} />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter client / user / profile / request"
          style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12.5, minWidth: 220 }} />
        <button onClick={onReload} disabled={loading} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 12px', fontSize: 12.5, fontWeight: 600, cursor: loading ? 'wait' : 'pointer' }}>
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Reload
        </button>
      </div>

      {error && <div style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 10 }}>{error}</div>}
      {loading && !data && <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Reading client copy logs from SAP…</div>}

      {data && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, fontSize: 12.5, color: '#475569' }}>
            <span><b>{fmt(sm.total)}</b> runs</span>·
            <span style={{ color: '#166534' }}><b>{fmt(sm.ok)}</b> successful</span>·
            <span style={{ color: sm.notOk ? '#b91c1c' : '#475569' }}><b>{fmt(sm.notOk)}</b> with warnings/errors</span>·
            <span>Last run: <b>{sm.lastRun || '—'}</b></span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {types.map(x => (
              <button key={x} onClick={() => setType(x)} style={{ padding: '4px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: '1px solid ' + (type === x ? '#2563eb' : '#e2e8f0'), background: type === x ? '#eff6ff' : '#fff', color: type === x ? '#1d4ed8' : '#475569' }}>
                {x}{x !== 'All' ? ` (${sm.byType[x]})` : ''}
              </button>
            ))}
          </div>
          {rows.length === 0 ? <div style={{ padding: '1.75rem', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No client copy runs found.</div> : (
            <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  {['Start', 'Type', 'Target', 'Source', 'Profile', 'User', 'Tables', 'Runtime', 'Status', 'Request'].map(h => <th key={h} style={th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id} onClick={() => setOpen(r)} style={{ cursor: 'pointer' }}>
                      <td style={td}>{r.start || '—'}</td>
                      <td style={td}>{r.type}{r.testMode ? ' (test)' : ''}</td>
                      <td style={{ ...td, fontFamily: 'monospace' }}>{r.targetClient}</td>
                      <td style={{ ...td, fontFamily: 'monospace' }}>{r.sourceSystem}{r.sourceClient ? '/' + r.sourceClient : ''}</td>
                      <td style={td}>{r.profile || '—'}</td>
                      <td style={td}>{r.user}</td>
                      <td style={td}>{fmt(r.tablesDone)}/{fmt(r.tablesTotal)}</td>
                      <td style={td}>{fmtDur(r.runtimeSec)}</td>
                      <td style={td}><ClientCopyStatus r={r} /></td>
                      <td style={{ ...td, fontFamily: 'monospace' }}>{r.request || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <DetailSheet open={!!open} onClose={() => setOpen(null)}
        title={open ? `Client ${open.targetClient} · ${open.type}` : ''}
        badge={open ? <ClientCopyStatus r={open} /> : null}
        fields={open ? [
          { label: 'Start', value: open.start }, { label: 'Last update', value: open.lastUpdate },
          { label: 'Runtime', value: fmtDur(open.runtimeSec) },
          { label: 'Target client', value: open.targetClient }, { label: 'Source client', value: open.sourceClient },
          { label: 'Source system / RFC dest.', value: open.rfcDest }, { label: 'Profile', value: open.profile },
          { label: 'User', value: open.user }, { label: 'Server', value: open.server },
          { label: 'Tables processed', value: `${fmt(open.tablesDone)} / ${fmt(open.tablesTotal)}` },
          { label: 'Inserts', value: fmt(open.inserts) }, { label: 'Updates', value: fmt(open.updates) }, { label: 'Deletes', value: fmt(open.deletes) },
          { label: 'Test mode', value: open.testMode ? 'Yes' : 'No' },
          { label: 'Status code', value: `${open.status} (run: ${open.statusRaw || '—'})` },
          { label: 'Error marker', value: open.ccError },
          { label: 'Request / log', value: open.request },
        ] : []}
      />
    </div>
  );
}
